import { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import Layout from "../components/Layout";
import { PageHeader, StatusBadge, EmptyState } from "../components/Shared";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TrendUp, TrendDown, Bank, ArrowsLeftRight, CalendarCheck, Money, ArrowUp, ArrowDown, Wallet,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const KpiCard = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const tones = {
    default: "text-foreground", positive: "text-emerald-400", negative: "text-rose-400",
  };
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon size={20} className="text-muted-foreground" />
      </div>
      <p className={`font-mono text-2xl font-semibold ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/dashboard/summary");
      setData(res.data);
    } catch (e) { toast.error("Veriler yüklenemedi"); }
    finally { setLoading(false); }
  };

  const seed = async () => {
    try {
      await api.post("/seed");
      toast.success("Örnek veriler oluşturuldu");
      setLoading(true);
      load();
    } catch (e) { toast.error("Hata oluştu"); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Layout><div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;

  const conv = data?.converted || {};
  const base = data?.base_currency || "GBP";
  const hasData = data?.by_currency.length > 0;

  return (
    <Layout>
      <PageHeader
        title="Genel Bakış"
        subtitle="Tüm tutarlar ana para biriminiz (GBP) cinsinden gösterilir"
        action={
          <div className="flex items-center gap-2 bg-card border border-border rounded-md px-4 py-2" data-testid="base-currency-badge">
            <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Ana Birim</span>
            <span className="font-mono font-semibold text-primary">£ GBP</span>
          </div>
        }
      />

      {!hasData ? (
        <div className="space-y-6">
          <EmptyState icon={Wallet} title="Henüz veri yok" desc="Başlamak için örnek verilerle uygulamayı keşfedin veya kendi kayıtlarınızı ekleyin." />
          <div className="flex justify-center">
            <Button data-testid="seed-button" onClick={seed}>Örnek Verilerle Başla</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard testid="kpi-balance" label="Toplam Kasa (GBP)" value={fmtMoney(conv.cash, base)} sub={`${data.counts.banks} hesap · GBP karşılığı`} icon={Bank} />
            <KpiCard testid="kpi-income" label="Toplam Gelir (GBP)" value={fmtMoney(conv.income, base)} sub="Tüm birimler dahil" icon={TrendUp} tone="positive" />
            <KpiCard testid="kpi-expense" label="Toplam Gider (GBP)" value={fmtMoney(conv.expense, base)} sub="Tüm birimler dahil" icon={TrendDown} tone="negative" />
            <KpiCard testid="kpi-net" label="Net Nakit Akışı (GBP)" value={fmtMoney(conv.net, base)} sub={conv.net >= 0 ? "Pozitif akış" : "Negatif akış"} icon={ArrowsLeftRight} tone={conv.net >= 0 ? "positive" : "negative"} />
          </div>

          <div className="mb-6" data-testid="currency-breakdown">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Para Birimi Bazında Kasa</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.by_currency.map((c) => (
                <div key={c.currency} data-testid={`breakdown-${c.currency}`} className="bg-card border border-border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono font-semibold">{c.currency}</span>
                    <span className="text-xs text-muted-foreground">1 = {c.rate} £</span>
                  </div>
                  <p className="font-mono text-lg font-semibold">{fmtMoney(c.bank_balance, c.currency)}</p>
                  <p className={`text-xs mt-1 ${c.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>Net {fmtMoney(c.net, c.currency)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 bg-card border border-border rounded-md p-6" data-testid="cashflow-chart">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-lg font-semibold">Nakit Akışı</h2>
                  <p className="text-sm text-muted-foreground">Son 6 ay gelir / gider (GBP)</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.monthly}>
                  <defs>
                    <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} width={40} />
                  <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }} labelStyle={{ color: "#FAFAFA" }} />
                  <Area type="monotone" dataKey="income" stroke="#10B981" fill="url(#inc)" strokeWidth={2} name="Gelir" />
                  <Area type="monotone" dataKey="expense" stroke="#F43F5E" fill="url(#exp)" strokeWidth={2} name="Gider" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-card border border-border rounded-md p-6" data-testid="receivable-payable">
              <h2 className="font-display text-lg font-semibold mb-6">Alacak & Borç</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <ArrowDown size={16} className="text-emerald-400" /> Beklenen Tahsilat
                  </div>
                  <p className="font-mono text-2xl font-semibold text-emerald-400">{fmtMoney(conv.receivable, base)}</p>
                </div>
                <div className="h-px bg-border" />
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <ArrowUp size={16} className="text-rose-400" /> Yaklaşan Ödeme
                  </div>
                  <p className="font-mono text-2xl font-semibold text-rose-400">{fmtMoney(conv.payable, base)}</p>
                </div>
                <div className="h-px bg-border" />
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div><p className="text-xs text-muted-foreground">Bekleyen Çek</p><p className="font-mono text-lg font-semibold">{data.counts.pending_checks}</p></div>
                  <div><p className="text-xs text-muted-foreground">İşlem Sayısı</p><p className="font-mono text-lg font-semibold">{data.counts.transactions}</p></div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-md p-6" data-testid="upcoming-payments-widget">
              <div className="flex items-center gap-2 mb-5">
                <CalendarCheck size={20} className="text-primary" />
                <h2 className="font-display text-lg font-semibold">Yaklaşan Ödeme & Tahsilatlar</h2>
              </div>
              {data.upcoming_payments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Yaklaşan kayıt yok</p>
              ) : (
                <div className="space-y-3">
                  {data.upcoming_payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm font-medium">{p.title}</p>
                        <p className="text-xs text-muted-foreground">{p.due_date} · {p.days_left < 0 ? "gecikmiş" : `${p.days_left} gün`}</p>
                      </div>
                      <p className={`font-mono text-sm font-semibold ${p.direction === "receivable" ? "text-emerald-400" : "text-rose-400"}`}>
                        {p.direction === "receivable" ? "+" : "-"}{fmtMoney(p.amount, p.currency)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-md p-6" data-testid="pending-checks-widget">
              <div className="flex items-center gap-2 mb-5">
                <Money size={20} className="text-primary" />
                <h2 className="font-display text-lg font-semibold">Bekleyen Çekler</h2>
              </div>
              {data.pending_checks.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Bekleyen çek yok</p>
              ) : (
                <div className="space-y-3">
                  {data.pending_checks.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm font-medium">Çek #{c.check_no} <span className="text-xs text-muted-foreground">· {c.type === "received" ? "Alınan" : "Verilen"}</span></p>
                        <p className="text-xs text-muted-foreground">{c.bank_name} · {c.due_date}</p>
                      </div>
                      <p className="font-mono text-sm font-semibold">{fmtMoney(c.amount, c.currency)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
